import { getGetApiDeviceQueryOptions, type DeviceViewDTO } from '@smartsash/api-client';
import { defineDataScreen, pagedQuery } from '@smartsash/data-screen';
import { Badge, Button, Icon } from '@smartsash/ui';
import { Link, useNavigate } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import styles from './Devices.module.css';
import { DevicesFilter } from './DevicesFilter';
import {
  devicesColumnText,
  devicesQueryParams,
  devicesSearchSchema,
  type DevicesColumnId,
} from './devicesModel';
import MobileCard from './MobileCard';
import RowActions from './RowActions';

import { useDataScreenTranslate } from '@/hooks/useDataScreenTranslate';
import { useLabNames } from '@/hooks/useLookupNames';
import { CopyableId } from '@/ui/shared/CopyableId';
import { EMPTY_VALUE } from '@/utils/emptyValue';
import { firstNonEmpty } from '@/utils/firstNonEmpty';

/** Headers render rather than resolve: a module-scope definition cannot call `t`. */
function ColumnHeader({ column }: { column: DevicesColumnId }) {
  const { t } = useTranslation();
  return <>{t(`devices.columns.${column}`)}</>;
}

/**
 * A version chip, or a dash when the device has not reported one.
 *
 * These columns used to render `apiVersion`/`clientAppVersion` under the design's `Sash API`/`Sash
 * client` headers. The API leaves both null on every device — nothing populates them — while
 * `firmwareVersion`/`osVersion` do carry values, so the columns show those instead and say so; see
 * `docs/design-audit/10-list-table-contract.md`. The badge is conditional because it used to render
 * regardless, producing a chip with an icon and no text — which reads as broken rather than absent.
 */
function VersionCell({ version }: { version: string }) {
  if (!version) return <>{EMPTY_VALUE}</>;
  return (
    <Badge variant="modern" size="sm" iconLeading={<Icon name="settings-02" />}>
      {version}
    </Badge>
  );
}

/**
 * `firmwareVersion`/`osVersion` reuse the detail page's existing `devices.details.*` header keys
 * rather than adding `devices.columns.*` ones, since the headers deliberately depart from the Figma
 * frame's `Sash API`/`Sash client` labels — see `VersionCell`.
 */
function VersionColumnHeader({ column }: { column: 'firmwareVersion' | 'osVersion' }) {
  const { t } = useTranslation();
  return <>{t(`devices.details.${column}`)}</>;
}

/** The model name, linking into the device, or a placeholder when the device reported none. */
function ModelCell({ row }: { row: DeviceViewDTO }) {
  const { t } = useTranslation();
  return (
    <Link to="/devices/$id" params={{ id: row.id ?? '' }} className={styles['company-cell']}>
      <span className={styles['company-name']}>
        {firstNonEmpty(row.model, t('devices.unknownModel'))}
      </span>
    </Link>
  );
}

const columns: ColumnDef<DeviceViewDTO>[] = [
  {
    /**
     * The device's database id (HS-1216).
     *
     * It is on screen for one operational reason: registering a fume hood means creating the
     * device here and then running `update-device-id.sh` on the hood over SSH with *this* value —
     * `Identifier` and `Serial number` are not what that script takes. So it is shown in full,
     * wrapped over two lines, with a copy button beside it rather than elided to fit.
     *
     * Deliberately **not** sortable: `GET /api/Device` rejects `sortBy=id`, its whitelist being
     * `model, serialNumber, identifier, firmwareVersion, osVersion, createdAt`
     * (`sash-admin-api/AdminAPI/Controllers/DeviceController.cs:41-50`). A header that sorts
     * nothing is the defect HS-1241 was filed for.
     *
     * Deliberately **not** a filter-builder field either — see the note in `DevicesFilter.tsx`.
     */
    id: 'id',
    header: () => <ColumnHeader column="id" />,
    cell: ({ row }) => <CopyableId id={devicesColumnText.id(row.original)} />,
    meta: { width: '200px', value: devicesColumnText.id },
  },
  {
    id: 'model',
    header: () => <ColumnHeader column="model" />,
    /*
     * HS-1216: the cell used to carry the identifier on a second line under the model name. It was
     * a P2-02 stand-in for the database key, and now that the key has its own column the sub-line
     * is one identifier too many — `Identifier` and `Serial Number` are both already columns of
     * their own. A device that reported no model shows the fallback rather than an empty cell.
     */
    cell: ({ row }) => <ModelCell row={row.original} />,
    meta: { primary: true, sortable: true, value: devicesColumnText.model },
  },
  {
    id: 'laboratory',
    header: () => <ColumnHeader column="laboratory" />,
    cell: ({ row, table }) => (
      <Badge variant="modern" size="sm" iconLeading={<Icon name="lab" />}>
        {devicesColumnText.laboratory(row.original, table.options.meta)}
      </Badge>
    ),
    meta: { width: '220px', value: devicesColumnText.laboratory },
  },
  {
    id: 'serialNumber',
    header: () => <ColumnHeader column="serialNumber" />,
    cell: ({ row }) => row.original.serialNumber,
    meta: { width: '160px', sortable: true, value: devicesColumnText.serialNumber },
  },
  {
    id: 'identifier',
    header: () => <ColumnHeader column="identifier" />,
    cell: ({ row }) => row.original.identifier,
    meta: { width: '200px', sortable: true, value: devicesColumnText.identifier },
  },
  {
    id: 'firmwareVersion',
    header: () => <VersionColumnHeader column="firmwareVersion" />,
    cell: ({ row }) => <VersionCell version={devicesColumnText.firmwareVersion(row.original)} />,
    meta: { width: '140px', sortable: true, value: devicesColumnText.firmwareVersion },
  },
  {
    id: 'osVersion',
    header: () => <VersionColumnHeader column="osVersion" />,
    cell: ({ row }) => <VersionCell version={devicesColumnText.osVersion(row.original)} />,
    meta: { width: '120px', sortable: true, value: devicesColumnText.osVersion },
  },
];

function AddDeviceButton({ iconOnly = false }: { iconOnly?: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const goToNew = () => {
    void navigate({ to: '/devices/new' });
  };

  return iconOnly ? (
    <Button
      hierarchy="primary"
      size="sm"
      iconLeading={<Icon name="plus" />}
      iconOnly
      aria-label={t('devices.addAriaLabel')}
      onClick={goToNew}
    />
  ) : (
    <Button hierarchy="primary" size="sm" iconLeading={<Icon name="plus" />} onClick={goToNew}>
      {t('devices.add')}
    </Button>
  );
}

/**
 * The Devices list on `@smartsash/data-screen` (HS-869, slice 4 of the plan's entity order).
 *
 * `searchScope`/`sortScope` are the factory's `'server'` default: `GetApiDeviceParams` now accepts
 * `search`, `sortBy`/`sortDir` and seven repeatable scope filters — the most of any of the five
 * screens — read out of the URL's `filters` through `devicesQueryParams`, shared with `DevicesFilter`
 * so both read the same cache entry.
 */
export const devicesScreen = defineDataScreen({
  entity: 'device',
  searchSchema: devicesSearchSchema,
  useTranslate: useDataScreenTranslate,
  list: {
    query: (search) =>
      pagedQuery<DeviceViewDTO>(getGetApiDeviceQueryOptions(devicesQueryParams(search))),
    columns,
    getRowId: (row) => row.id ?? '',
    useTableMeta: () => ({ labNames: useLabNames() }),
    mobileCard: (row) => <MobileCard row={row} />,
    titleKey: 'devices.title',
    emptyState: {
      titleKey: 'devices.empty.title',
      descriptionKey: 'devices.empty.description',
      filteredTitleKey: 'list.emptyFiltered.title',
      filteredDescriptionKey: 'list.emptyFiltered.description',
    },
    selectable: true,
    rowActions: [{ id: 'row-actions', render: (row) => <RowActions row={row} /> }],
    headerAction: { id: 'add', permission: 'device.create', render: () => <AddDeviceButton /> },
    mobileHeaderAction: {
      id: 'add-mobile',
      permission: 'device.create',
      render: () => <AddDeviceButton iconOnly />,
    },
    filterSlot: (search) => <DevicesFilter search={search} />,
  },
});
